// src/pages/ExampleReport.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { FloatingNav } from '@/components/report/FloatingNav';
import { ReportHeader } from '@/components/report/ReportHeader';
import { ExampleReportContent } from '../components/example-report/ExampleReportContent';
import { ArrowLeft, Monitor } from 'lucide-react';
import Logo from '@/components/Logo';
import LoadingScreen from '@/components/common/LoadingScreen';
import { ExampleAuthProvider } from '@/contexts/ExampleAuthProvider';
import { db, collections } from '@/lib/firebase';
import { ReportData } from '@/types/report';
import { getDoc, doc } from 'firebase/firestore';
import { transformReportData } from '@/services/report/transforms';
import { validateReportData } from '@/services/report/validation';

// Define tab visibility configurations based on plan
const planTabConfigurations = {
  'plan-0': ['general', 'self-assessment', 'evaluators', 'grow'],
  'plan-1': ['general', 'self-assessment', 'evaluators', 'grow'],
  'plan-2': ['general', 'self-assessment', 'evaluators', 'grow', 'leadership'],
  'plan-3': ['general', 'self-assessment', 'evaluators', 'grow', 'career'],
  'plan-4': ['general', 'self-assessment', 'evaluators', 'grow', 'collaborator'],
  'direct': ['general', 'self-assessment', 'evaluators', 'grow', 'leadership', 'career', 'collaborator']
};

// Helper function to determine source from URL or referrer
const determineSource = (search: string, referrer: string | null): string => {
  // Check URL parameters first
  const params = new URLSearchParams(search);
  const planParam = params.get('plan');
  
  if (planParam && Object.keys(planTabConfigurations).includes(planParam)) {
    return planParam;
  }
  
  // Check referrer if no valid plan parameter
  if (referrer) {
    // Extract plan ID from referrer if it contains one
    const planMatch = referrer.match(/plan-[0-4]/);
    if (planMatch && planMatch[0]) {
      return planMatch[0];
    }
  }
  
  // Default to direct access if no source determined
  return 'direct';
};

export default function ExampleReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('general');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessSource, setAccessSource] = useState<string>('direct');
  const [visibleTabs, setVisibleTabs] = useState<string[]>([]);

  // Determine access source on component mount
  useEffect(() => {
    const source = determineSource(
      location.search,
      document.referrer
    );
    
    // Store source in session storage for persistence across refreshes
    sessionStorage.setItem('reportAccessSource', source);
    setAccessSource(source);
    
    // Set visible tabs based on source
    setVisibleTabs(planTabConfigurations[source] || planTabConfigurations.direct);
  }, [location.search]);

  // Restore source from session storage on page refresh
  useEffect(() => {
    const storedSource = sessionStorage.getItem('reportAccessSource');
    if (storedSource && storedSource !== accessSource) {
      setAccessSource(storedSource);
      setVisibleTabs(planTabConfigurations[storedSource] || planTabConfigurations.direct);
    }
  }, []);

  // Handle tab change with visibility check
  const handleTabChange = (tab: string) => {
    if (visibleTabs.includes(tab)) {
      setActiveTab(tab);
    } else {
      // Fallback to first visible tab if requested tab is not visible
      setActiveTab(visibleTabs[0] || 'general');
    }
  };

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        const docRef = doc(db, collections.reports, 'example-data');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const docData = docSnap.data();
          
          // Use the mock data if no reportData exists
          if (!docData.reportData) {
            setReportData({
              questions: [
                {
                  id: 1,
                  text: "How effectively does the subject perform in their current role?",
                  selfAssessment: 7,
                  evaluators: [8, 9, 8, 9],
                  visualization: "donut"
                },
                {
                  id: 2,
                  text: "How much do you value the subject's contributions to the team?",
                  selfAssessment: 9,
                  evaluators: [7, 6, 7, 8],
                  visualization: "spider"
                },
                {
                  id: 3,
                  text: "How does the subject contribute to creating a positive workplace environment?",
                  selfAssessment: 6,
                  evaluators: [8, 9, 8, 9],
                  visualization: "donut"
                }
              ],
              feedbackComments: {
                1: [
                  "Consistently delivers high-quality work and meets deadlines",
                  "Shows strong problem-solving abilities",
                  "Could improve on documenting processes"
                ],
                2: [
                  "Brings valuable insights to team discussions",
                  "Actively participates in knowledge sharing",
                  "Technical expertise is highly valued"
                ],
                3: [
                  "Creates an inclusive atmosphere",
                  "Always willing to help others",
                  "Could be more proactive in team events"
                ]
              },
              metadata: {
                generatedAt: new Date(),
                updatedAt: new Date(),
                status: 'completed',
                version: '1.0'
              }
            });
            setLoading(false);
            return;
          }

          try {
            const parsedData = typeof docData.reportData === 'string'
              ? JSON.parse(docData.reportData)
              : docData.reportData;
            
            const transformedData = transformReportData(parsedData);
            validateReportData(transformedData);
            setReportData(transformedData);
          } catch (parseError) {
            console.error('Error parsing report data:', parseError);
            throw new Error('Invalid report data format');
          }
        } else {
          setError('Example report data not found');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load example report';
        console.error('Error fetching report data:', err);
        setError(errorMessage);
      }
      setLoading(false);
    };

    fetchReportData();
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !reportData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex justify-between items-center px-4 sm:px-6 lg:px-8 py-4 bg-white shadow-sm">
          <Logo className="h-8" />
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-dark px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors flex items-center space-x-2 font-medium shadow-sm hover:shadow-md"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg max-w-md text-center">
            <p className="mb-4">{error || 'No example report data available'}</p>
            <button
              onClick={() => navigate('/')}
              className="text-primary hover:text-primary-hover underline"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // Wrap your content with the ExampleAuthProvider so that all nested components use the dummy user.
    <ExampleAuthProvider>
      <div className="min-h-screen bg-gray-50">
        {/* Small Screen Message */}
        <div className="hidden max-[700px]:flex fixed inset-0 z-50 bg-white flex-col items-center justify-center p-6 text-center">
          <Monitor className="w-16 h-16 text-primary mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-3">Larger Screen Required</h2>
          <p className="text-gray-600 max-w-md">
            To ensure the best experience and fully maximize the value of this report example, please access it from a larger screen device.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 bg-primary text-dark px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors"
          >
            Return to Home
          </button>
        </div>

        <div className="flex justify-between items-center px-4 sm:px-6 lg:px-8 py-4 bg-white shadow-sm">
          <Logo className="h-8" />
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-dark px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors flex items-center space-x-2 font-medium shadow-sm hover:shadow-md"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
        </div>

        <div className="hidden min-[701px]:block">
          <FloatingNav 
            activeTab={activeTab} 
            onTabChange={handleTabChange} 
            visibleTabs={visibleTabs}
          />
        </div>

        <main className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow">
              <div className="hidden min-[701px]:block">
                <ReportHeader 
                  activeTab={activeTab} 
                  onTabChange={handleTabChange}
                  visibleTabs={visibleTabs}
                />
                
                <ExampleReportContent 
                  activeTab={activeTab}
                  reportData={reportData}
                  visibleTabs={visibleTabs}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </ExampleAuthProvider>
  );
}